<template>
    <li
        class="option"
        :class="{'option-disabled':disabled}"
        :style="styleInSide"
        @click.stop="!disabled?(hasChildren ? toggleChildren() : handleClick()):null"
        :key="currentOption.id"
    >
        <div class="option_dropdown_item" :class="{selected: isSingleSelector && (selected), 'has-children': hasChildren}" >
            <div class="option_dropdown_item_label" :title='currentOption.label'>
                <i v-if="hasChildren && !isMobile" class="triangle icon-icon_down-2" :class="{ 'reverse': showSubList }"></i>
                <img v-if="showIcon && currentOption.id!==-2" :src="iconUrl">
                <p>{{ currentOption.label }}</p>
            </div>
            <div v-if="!isSingleSelector" @click.stop>
                <!-- @click.stop 防止点击复选框时触发 li 的点击事件 -->
                <reo-old-checkbox
                    class='multipleSelect'
                    size='middle'
                    :disabled="disabled"
                    :value="selected"
                    :indeterminate="indeterminate"
                    @input="handleClick"
                ></reo-old-checkbox>
            </div>
            <div
                v-if="isSingleSelector && isMobile && selected"
                class="option_dropdown_item_checked"
            >
                <icon-right></icon-right>
            </div>
        </div>
        <!-- 递归渲染子选项 -->

        <ul
            v-if="hasChildren && showSubList"
            class="child-ul"
            @click.stop
        >
            <template v-if="!isMobile">
                <tree-option
                    v-for="child in childrenOptions"
                    class="child-option"
                    :class="{disabled: !child.selected && reachMaxSelect}"
                    :key="child.id"
                    :current-option="child"
                    :show-icon="showIcon"
                    :data-source="dataSource"
                    :prop-selection="propSelection"
                    :selector-type="selectorType"
                    :reachMaxSelect="reachMaxSelect"
                    :width-in="widthIn"
                    :height-in="heightIn"
                    :font-size-in="fontSizeIn"
                    @select="(selected, option)=>handleChildClick(selected, option)"
                ></tree-option>
            </template>
            <template v-else>
                <tree-option-mobile
                    v-for="child in childrenOptions"
                    class="child-option"
                    :class="{disabled: !child.selected && reachMaxSelect}"
                    :key="child.id"
                    :current-option="child"
                    :data-source="dataSource"
                    :prop-selection="propSelection"
                    :selector-type="selectorType"
                    :reachMaxSelect="reachMaxSelect"
                    :width-in="widthIn"
                    :height-in="heightIn"
                    :font-size-in="fontSizeIn"
                    @select="(selected, option)=>handleChildClick(selected, option)"
                ></tree-option-mobile>
            </template>
        </ul>
    </li>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { config } from 'CONFIG';
import { ESelectorType, IReoSelectOption } from '@reolink-cloud/interface';
import ReoOldCheckbox from './Checkbox.vue';
import IconRight from '@/components/Svg/IconRight.vue';
import OtherAnimalIcon from 'IMAGE/dashboard/animalIcons/animal_other.svg';
import TreeOptionMobile from './TreeOptionMobile.vue';
import { productsManager } from '@/lib';

export default Vue.extend({
    name: 'TreeOption',
    components: {
        TreeOptionMobile,
        ReoOldCheckbox,
        IconRight
    },
    props: {
        selectorType: {
            type: String as PropType<ESelectorType>,
            default: 'single',
        },
        showIcon: {
            type: Boolean,
            default: false
        },
        dataSource: {
            type: Array as PropType<IReoSelectOption[]>,
            required: true,
        },
        currentOption: {
            type: Object as PropType<IReoSelectOption>,
            required: true
        },
        propSelection: {
            type: Array as PropType<Array<string | number>>,
            default: () => [],
        },
        widthIn: {
            type: [String, Number],
            default: -1
        },
        heightIn: {
            type: [String, Number],
            default: 0.3
        },
        fontSizeIn: {
            type: [String, Number],
            default: 0.16
        },
        maxSelect: {
            type: Number,
            default: -1
        },
        reachMaxSelect: Boolean,
        allOptionDisableOthers: {
            type: Boolean,
            default: false
        }
    },
    data() {

        return {
            /** 初始化子选项的显示状态 */
            showSubList: true,

            selected: this.currentOption.selected ?? false,

        };

    },
    computed: {
        isSingleSelector(): boolean {

            return this.selectorType === ESelectorType.SINGLE;

        },
        childrenOptions(): IReoSelectOption[] {

            if ( this.isSingleSelector ) {

                return [];

            } else {

                return this.dataSource.filter( option => option.parentId === this.currentOption.id );

            }

        },
        hasChildren(): boolean {

            return this.childrenOptions.length > 0;

        },
        /** 选项列表的样式 */
        styleInSide(): Record<string, string> {

            const oemOptionColor = config.oem === 'snapeak' ?
                ( this.selected && this.isSingleSelector ? '#1B907D' : '#f7f8f9' ) :
                ( this.selected && this.isSingleSelector ? 'var(--b_6)' : 'var(--font_primary)' );

            const oemOptionFontWeight = config.oem === 'snapeak' ?
                '400' :
                ( this.selected && this.isSingleSelector ? '500' : '400' );

            return {
                '--option-height': this.heightIn === -1 ? 'auto' : Number( this.heightIn ) * 100 + 'px',
                '--option-width': this.widthIn === -1 ? '100%' : Number( this.widthIn ) * 100 + 'px',
                '--option-font-size': Number( this.fontSizeIn ) * 100 + 'px',
                '--option-color': oemOptionColor,
                '--option-overflow': 'hidden',
                '--option-font-weight': oemOptionFontWeight
            };

        },
        // 父选项是否处于中间状态
        indeterminate(): boolean {

            if ( this.currentOption.value === 'all' ) {

                const allSelected = this.dataSource.filter( option => option.value !== 'all' ).every( _option => _option.selected ) ;
                const allUnselected = this.dataSource.filter( option => option.value !== 'all' ).every( _option => !_option.selected ) ;
                return !allSelected && !allUnselected;

            }

            return (
                this.hasChildren &&
                this.childrenOptions.some(( child ) => child.selected ) &&
                !this.childrenOptions.every( child => child.selected )
            );

        },
        disabled(): boolean {

            const allOption = this.dataSource.find( _option => _option.value === 'all' );
            if ( this.allOptionDisableOthers && ( allOption?.selected ?? false )) {

                return this.currentOption.value !== 'all';

            }
            return !this.selected && this.reachMaxSelect && this.currentOption.value !== 'all';

        },
        isMobile(): boolean {

            return this.$store.getters.isMobile;

        },
        iconUrl(): string {

            const url = productsManager.getAlarmTypeInfoByKey( this.currentOption.value as string )?.meta?.iconUrl ?? '';
            if ( url && url !== '' ) {

                return url;

            } else {

                return OtherAnimalIcon;

            }

        }

    },
    watch: {
        'currentOption.selected'( newVal: boolean ) {

            this.selected = newVal;

        },
    },
    methods: {

        handleClick() {

            if ( this.isSingleSelector ) {

                this.selected = true;
                this.$emit( 'select', true, this.currentOption );

            } else {

                this.selected = !this.selected;
                this.$emit( 'select', this.selected, this.currentOption );

            }

        },

        handleChildClick( selected: boolean, childOption: IReoSelectOption ) {

            this.$emit( 'select', selected, childOption );

        },

        toggleChildren() {

            this.showSubList = !this.showSubList;

        },
    },
});
</script>

<style scoped lang="less">
.option {
    list-style: none;
    width: var(--option-width);
    @media @snapeak_mobile{
        list-style:none !important;
    }
    &.child-option{
        .option_dropdown_item > div:first-child{
            margin-left: 32px;
        }
    }
    @media @snapeak_mobile{
        .child-ul{
            margin: 0 16px;
            padding: 12px;
            background-color: @bg_03;
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        }
    }
    &.option-disabled{
        .option_dropdown_item{
            cursor: not-allowed;
        }
        cursor: not-allowed;
    }
}

.option_dropdown_item {
    height: 100%;
    font-size: var(--option-font-size);
    line-height: var(--option-height);
    color: var(--option-color);
    cursor: pointer;
    padding: 0 16px;
    overflow: var(--option-overflow);
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    display: flex;
    justify-content: space-between;
    @media @snapeak_mobile{
        margin: 0 16px;
        padding: 16px 0;
        border-bottom: 1px solid @line;
        &.has-children{
            border-bottom: none;
        }
    }
    .option_dropdown_item_label{
        display: flex;
        gap: 4px;
        align-items: center;
        overflow: hidden;
        .triangle {
            display: inline-block;
            font-size: 12px;
            line-height: var(--option-height);
            text-align: center;
            transform: rotate(-90deg);
            transition: all 0.4s;
            margin-right: 4px;
            &.reverse {
                transform: rotate(0deg);
            }
        }
        img{
            width: 20px;
            height: 20px;
        }
        p{
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            font-weight: var(--option-font-weight);
        }
    }
    .option_dropdown_item_checked{
        display: flex;
        align-items: center;
        svg{
            width: 22px;
            height: 22px;
        }
    }
    .multipleSelect{
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        & /deep/ .reo-checkbox.original{
            &.no-checked {
                background-color: transparent;
            }
            &.disabled.checked{
                background-color: @brand_light;
                border-color: @brand_light;
            }
        }

        @media @snapeak_pc{
            & /deep/ span.text{
                margin:0;
            }
        }
        @media @snapeak_mobile{
            flex-direction: row-reverse;
            justify-content: start;
        }
    }
    @media @snapeak_pc{
        &:hover {
            background-color: @select-list-item-hover-background;
            /deep/ .reo-checkbox{
                border-color: @brand_light;
            }
        }
    }
    @media @snapeak_mobile{
        &.active{
            background-color: @select-list-item-click-background;
            /deep/ .reo-checkbox{
                border-color: @brand_light;
            }
        }
    }
}

.toggle {
    cursor: pointer;
    margin-left: 10px;
}
</style>
